import React, { useState, useMemo, useEffect } from 'react';
import useProductShopData from './useProductShopData';
import { placeholderFor } from './productShopMapper';
import { BottleGlyph } from './ProductCard';
import './ProductShopPreview.css';

// Inline "Shop" section for the salon home page — mirrors the Services section
// (same `.category-tabs` + `.view-all-button`) and the product grid styling, so
// it sits natively in the page. Shows a small preview of the salon's online
// products; tapping a product or "View Shop" opens the full storefront modal.
// Renders nothing when the salon has no products marked to sell online.

const PREVIEW_COUNT = 4;

const ProductShopPreview = ({ salonIdOrSlug, onOpenShop, onOpenProduct }) => {
  const { loading, products, categories, currency } = useProductShopData(salonIdOrSlug);
  const [tab, setTab] = useState('Featured');

  useEffect(() => {
    if (!categories.includes(tab)) setTab('Featured');
  }, [categories, tab]);

  const shown = useMemo(() => {
    const list = tab === 'Featured' ? products : products.filter((p) => p.category === tab);
    return list.slice(0, PREVIEW_COUNT);
  }, [products, tab]);

  // Hide the whole section when there's nothing to sell online.
  if (loading || products.length === 0) return null;

  return (
    <section className="section">
      <h2 className="section-title">Shop</h2>

      {categories.length > 1 && (
        <div className="category-tabs">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`category-tab ${tab === c ? 'active' : ''}`}
              onClick={() => setTab(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="products-grid">
        {shown.map((p) => {
          const img = p.images && p.images[0];
          return (
            <div
              key={p.id}
              className="product-card ps-preview-card"
              role="button"
              tabIndex={0}
              onClick={() => onOpenProduct(p.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') onOpenProduct(p.id); }}
            >
              {img ? (
                <img src={img} alt={p.name} className="product-image" />
              ) : (
                <div className="product-image ps-preview-ph" style={{ background: placeholderFor(p.name.length) }}>
                  <BottleGlyph size={32} />
                </div>
              )}
              <div className="product-info">
                <h3 className="product-name">{p.name}</h3>
                <p className="product-price">{currency}{p.price.toFixed(2)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <button type="button" className="view-all-button" onClick={onOpenShop}>View Shop</button>
    </section>
  );
};

export default ProductShopPreview;
